import { useId } from "react";

/**
 * The loading mark, built from the logo's own blocks. Three variants (orbit is the default), all
 * of which rest on the exact logo:
 * - handoff: a block rises into the ring's gap and docks on the left foot, the right leg's
 *   bottom block drops away, and the new block slides into its place.
 * - flow: the logo rests as dimmed pixels and a short lit run jumps pixel to pixel around it,
 *   from the foot (the L end) to the bottom of the right leg (the I end).
 * - orbit: one orange block rises out of the foot's bottom edge, rides the whole ring once and
 *   sinks into the bottom of the right leg.
 * flow and orbit both move through PIXELS, clipped to the logo's outline, which keeps the
 * stepped corners and the chamfer; the orange block is always one rectangle, even mid-corner. Keyframes live in index.css
 * (orbit-*); reduced motion swaps them for a slow fade.
 */

const LOGO =
  "M31.4047 5.86244L26.2291 0.705547C25.8534 0.329756 25.3399 0.117676 24.8079 0.117676H8.29542C7.75964 0.117676 7.32432 0.552998 7.32432 1.08878V4.51183C7.32432 5.0476 6.889 5.48293 6.35322 5.48293H2.9711C2.43532 5.48293 2 5.91825 2 6.45403V23.759C2 24.2948 2.43532 24.7301 2.9711 24.7301H6.35694C6.89272 24.7301 7.32804 25.1654 7.32804 25.7012V29.1466C7.32804 29.6824 7.76336 30.1177 8.29914 30.1177H16.6781C17.214 30.1177 17.6493 29.6824 17.6493 29.1466V23.8371C17.6493 23.3014 17.214 22.866 16.6781 22.866H9.47488C8.9391 22.866 8.50378 22.4307 8.50378 21.8949V7.96463C8.50378 7.42885 8.9391 6.99353 9.47488 6.99353H24.5214C25.0571 6.99353 25.4925 7.42885 25.4925 7.96463V29.1429C25.4925 29.6786 25.9278 30.1139 26.4636 30.1139H31.0288C31.5646 30.1139 32 29.6786 32 29.1429V7.28747C32 6.75169 31.7879 6.23823 31.4084 5.86244H31.4047Z";

// The logo's corner radius; every moving block uses it so pixels look cut from the logo.
const RADIUS = 0.97;

// handoff: the right leg's bottom block (the one that leaves), and where the new one docks,
// one gap-width (7.84) to its left.
export const SLOT = { x: 25.49, y: 24.73, width: 6.51, height: 5.38, rx: RADIUS };
export const DOCK_X = 17.65;


// The logo cut into pixels, in the order blocks travel round it: [x, y, width, height]. All lie
// inside the logo; the corner pixels take the logo's steps.
export const PIXELS = [
  [12.3, 22.87, 5.35, 7.25], // foot (the L end)
  [2, 22.87, 9.5, 7.25], // bottom-left corner
  [2, 14.9, 6.5, 7.17], // left wall
  [2, 7.8, 6.5, 6.3],
  [2, 0, 9.5, 7], // top-left corner
  [12.3, 0, 6.3, 7], // top
  [19.4, 0, 5.3, 7],
  [25.49, 0, 6.51, 7], // top-right corner
  [25.49, 7.8, 6.51, 6.9], // right leg
  [25.49, 15.5, 6.51, 6.9],
  [25.49, 23.2, 6.51, 6.92] // the I end
];
// orbit: the block starts and ends one pixel-height below the two ends. The logo outline clips
// it, so it rises out of the foot's bottom edge and sinks into the leg's, never showing below.
const BELOW = 7.3;
export const ORBIT_PATH = [
  [PIXELS[0][0], PIXELS[0][1] + BELOW, PIXELS[0][2], PIXELS[0][3]],
  ...PIXELS,
  [PIXELS.at(-1)[0], PIXELS.at(-1)[1] + BELOW, PIXELS.at(-1)[2], PIXELS.at(-1)[3]]
];
// One jump per STEP_MS; index.css's orbit-flow keyframes are cut for PIXELS.length steps.
export const STEP_MS = 120;

const ORBIT_SECONDS = 2.4;
const NATIVE_ORANGE = "#FF7A1A";

/** Clips moving pixels to the logo's outline, so nothing ever shows outside it. */
function Ring({ id, children }) {
  return (
    <>
      <defs>
        <clipPath id={`orbit-clip-${id}`}>
          <path d={LOGO} />
        </clipPath>
      </defs>
      <g clipPath={`url(#orbit-clip-${id})`} data-testid="orbit-ring">
        {children}
      </g>
    </>
  );
}

function Handoff({ id }) {
  return (
    <>
      <defs>
        <clipPath id={`orbit-clip-${id}`}>
          {/* Everything but the slot, so the moving block stands in for the logo's own. */}
          <path clipRule="evenodd" d={`M0 0H32V32H0Z M${SLOT.x} ${SLOT.y}H32.1V30.2H${SLOT.x}Z`} />
        </clipPath>
      </defs>
      <path d={LOGO} clipPath={`url(#orbit-clip-${id})`} data-testid="orbit-ring" />
      <rect className="orbit-pixel-out" {...SLOT} data-testid="orbit-pixel-out" />
      <rect className="orbit-pixel-in" {...SLOT} x={DOCK_X} data-testid="orbit-pixel-in" />
    </>
  );
}

function Flow({ id }) {
  return (
    <Ring id={id}>
      {PIXELS.map(([x, y, width, height], i) => (
        <rect
          key={i}
          className="orbit-flow"
          x={x}
          y={y}
          width={width}
          height={height}
          rx={RADIUS}
          data-testid="orbit-pixel"
          style={{
            // Negative delays start every pixel mid-loop, so the first frame isn't blank.
            animationDelay: `${(i - PIXELS.length) * STEP_MS}ms`,
            animationDuration: `${PIXELS.length * STEP_MS}ms`
          }}
        />
      ))}
    </Ring>
  );
}

function Orbit({ id }) {
  return (
    <>
      <path d={LOGO} />
      <Ring id={id}>
        {/* SMIL glides one rectangle from pixel to pixel; CSS can't animate x/y/width/height
            in every browser. Reduced motion hides it (index.css). */}
        <rect className="orbit-rider" fill={NATIVE_ORANGE} rx={RADIUS} data-testid="orbit-rider">
          {["x", "y", "width", "height"].map((attribute, k) => (
            <animate
              key={attribute}
              attributeName={attribute}
              values={ORBIT_PATH.map((p) => p[k]).join(";")}
              dur={`${ORBIT_SECONDS}s`}
              repeatCount="indefinite"
            />
          ))}
        </rect>
      </Ring>
    </>
  );
}

const VARIANTS = { handoff: Handoff, flow: Flow, orbit: Orbit };

export default function OrbitLoader({ variant = "orbit", size = 32, label = "Loading", className = "text-[#0072DB]" }) {
  const id = useId().replace(/:/g, "");
  const Variant = VARIANTS[variant] || Handoff;
  return (
    <svg
      role="status"
      aria-label={label}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      overflow="visible"
      fill="currentColor"
      className={`orbit-loader shrink-0 ${className}`}
      data-variant={variant}
    >
      <Variant id={id} />
      <circle cx="13.5" cy="12.5" r="2.5" />
    </svg>
  );
}
